import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { RootState, useAppDispatch, useAppSelector } from '../../../../redux';
import { getChangelog, getDiff } from '../../../../redux';
import { Release } from '../../../../models';
import { Changelog, DiffView, Icon } from '../../../../components';
import { normalizeUiLanguage } from '../../../../utils/uiPreferences';

function PublicProjectView() {
    const { t, i18n } = useTranslation();
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();

    const { id, from, merge, to, file }    = useParams();
    const { changelog, diff, gettingDiff } = useAppSelector((state: RootState) => state.projects);

    let path = file || '';
    try {
        path = atob(path);
    } catch (err) {
        path = file || '';
    }

    useEffect(() => {
        const lang = normalizeUiLanguage(
            searchParams.get('lang') || searchParams.get('lng')
        );
        if (lang && i18n.language !== lang)
            i18n.changeLanguage(lang);
    }, [searchParams, i18n]);

    useEffect(() => {
        if (id && from && to)
            dispatch(getDiff(id, from, to, path));
        else if (id)
            dispatch(getChangelog(id, from, merge?.split(',')));
    }, [dispatch, id, from, merge, to, path]);

    if (!changelog?.project && !diff?.from && gettingDiff !== 'pending')
        return null;

    const project = changelog?.project ?? diff?.project;

    const { releases, merged, upcoming } = (changelog || {});

    return (
        <div className="ProjectView">
            <header className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-4 py-2 sm:px-6 lg:px-8">
                <div className="mx-auto lg:flex lg:itemProjects-center lg:justify-between">
                    <div className="flex flex-col space-y-0">
                        <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                            { project?.name }
                        </h2>
                        {merged?.length > 0 && (
                            <h3 className="">
                                <span className="text-md font-medium tracking-tight text-gray-900 dark:text-gray-100">&nbsp;
                                    {t('projects.includes')}&nbsp;
                                </span>
                                {merged?.map((mergedProject: AnyObj, i: number) => (
                                    <span key={mergedProject._id || mergedProject.name} className="text-md font-medium tracking-tight text-gray-900 dark:text-gray-100">
                                        {mergedProject.name}
                                        {i < merged.length - 2 ? ', ' : (i === merged.length - 2 ? ' ' + t('common.and') + ' ': '')}
                                    </span>
                                ))}
                                {from && project?.name && (
                                    <span className="text-md font-medium tracking-tight text-gray-900 dark:text-gray-100">
                                        &nbsp;{t('projects.includes_since', { version: from, project: project.name })}
                                    </span>
                                )}
                            </h3>
                        )}
                        {from && !to && id && (
                            <p className="text-md font-medium tracking-tight text-gray-900 dark:text-gray-100">
                                <Link
                                    to={`/public/projects/${id}/changelog`}
                                    className="underline hover:text-blue-700"
                                >
                                    {t('projects.view_full_changelog')}
                                </Link>
                            </p>
                        )}
                    </div>
                </div>
            </header>
            <main className="mx-3">
                {(releases?.length > 0) && (
                    <div className="-mx-4 flex-1 shrink">
                        <Changelog
                            releases={releases.sort((a: Release, b: Release) => b.versionNumber - a.versionNumber)}
                            upcoming={upcoming}
                            isPublic
                        />
                    </div>
                )}

                {(gettingDiff === 'pending') && (
                    <div className="flex justify-center items-center m-3">
                        <Icon type="loading" size={ 20 } />
                    </div>
                )}

                {diff?.from && (
                    <div className="mb-5">
                        <div className="text-center text-lg">
                            <span className="font-bold">{ from }</span> -&gt; <span className="font-bold"> { to }</span>
                        </div>
                        <div className="text-center text-md">
                            <span className="font-light">{ path }</span>
                        </div>
                        {(diff.content?.length > 0) ? (
                            <DiffView diff={ diff.content } />
                        ) : (
                            <div className="flex justify-center items-center m-3">
                                <Icon type="sad" size={8} />
                            </div>
                        )}
                    </div>
                )}
            </main>
        </div>
    );
}

export default PublicProjectView;
